import React, {useCallback, useEffect, useRef} from 'react';
import type {MultiThesaurusValue} from 'sceneExtensions/extensions/multiThesaurusEdit/types';
import type {FieldExtensionUnsubscribe} from 'sceneExtensions/types';
import {FieldExtensionEnum} from 'sceneExtensions/types';
import type {PartActionMultiThesaurus} from 'schemaDefinition/types';
import type {ThesaurusValue} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {ActionButton} from 'components/button';
import {FlexBox} from 'components/layout';
import {useFormSchemaGlobalScopeContext} from 'schema/contexts';
import {sortedEncodeMultiThesaurusValue} from 'schema/form/functions/sortedEncodeMultiThesaurusValue';
import {makePartPath} from 'schema/form/functions/validators';
import {useMultiThesaurusEditContext} from 'sceneExtensions/extensions/multiThesaurusEdit';
import {useIsCurrentExtension} from 'sceneExtensions/hooks';
import type {DataFormActionProps} from '../types';
import {useDataFormContext} from '../../contexts';
import {useThesaurusParts} from './useThesaurusParts';

export const MultiThesaurusAction: React.FC<
  DataFormActionProps<PartActionMultiThesaurus>
> = ({action, scopePath, useValue}) => {
  const {t} = useLocalization();
  const {id, getEditValue, setEditValue} = useDataFormContext();

  const {setSourceField} = useMultiThesaurusEditContext();

  const isCurrentExtension = useIsCurrentExtension(
    FieldExtensionEnum.multiThesaurusEdit,
    action.role,
  );

  const {schema} = useFormSchemaGlobalScopeContext();

  const parts = useThesaurusParts(schema, action.partNames);

  const unsubscribeRef = useRef<FieldExtensionUnsubscribe>(undefined);

  // Holds current edit value (sorted and encoded), updated when user adds/removes nodes from right panel
  // or when node is removed in the form (change of order is ignored)
  const sortedMultiEditValueRef = useRef<string | undefined>(undefined);

  const scopeValue = useValue(scopePath, id);

  const getMultiValue = useCallback(
    (
      thesaurusParts: typeof parts,
      getValue: typeof getEditValue,
      path: string | undefined,
      id: string,
    ) => {
      return thesaurusParts.reduce<MultiThesaurusValue>((acc, p) => {
        const partPath = makePartPath(path, p);
        const partValue = (getValue(partPath, id) ?? []) as ThesaurusValue;
        return {...acc, [p.thesaurusId]: partValue};
      }, {} as MultiThesaurusValue);
    },
    [],
  );

  const handleDisconnect = useCallback(() => {
    unsubscribeRef.current = undefined;
  }, []);

  const handleEditThesaurus = useCallback(() => {
    const value = getMultiValue(parts, getEditValue, scopePath, id);

    const thesauruses = parts.map(p => ({
      thesaurusId: p.thesaurusId,
      showCode: p.showCode,
    }));

    const handleSetValue = (value: MultiThesaurusValue | undefined = {}) => {
      parts.forEach(p => {
        const partPath = makePartPath(scopePath, p);
        const partValue = value[p.thesaurusId] ?? [];
        setEditValue(partPath, partValue, p);
      });
      sortedMultiEditValueRef.current = sortedEncodeMultiThesaurusValue(value);
    };

    sortedMultiEditValueRef.current = sortedEncodeMultiThesaurusValue(value);

    Promise.resolve(
      setSourceField(
        {
          // Use role as fieldId, will result in unmount and unsubscribe (close extension) when
          // the schema resolves to part with different role, e.g. when toggling
          // schoolbok, different Part will be active, and this component will unmount (re-mount)
          fieldId: action.role,
          currentValue: value,
          fieldConfig: {
            thesauruses,
          },
        },
        handleSetValue,
        handleDisconnect,
      ),
    ).then(unsubscribe => {
      unsubscribeRef.current = unsubscribe;
    });
  }, [
    getMultiValue,
    parts,
    getEditValue,
    scopePath,
    id,
    setSourceField,
    action.role,
    handleDisconnect,
    setEditValue,
  ]);

  // When scope value changes,
  // => if extension is open, update the edit value if it has changed
  useEffect(() => {
    if (isCurrentExtension) {
      const currentValue = getMultiValue(parts, getEditValue, scopePath, id);
      const encodedValue = sortedEncodeMultiThesaurusValue(currentValue);

      if (encodedValue !== sortedMultiEditValueRef.current) {
        handleEditThesaurus();
      }
    }
    // Trigger only when scopeValue changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeValue]);

  // On unmount, unsubscribe from the extension
  useEffect(() => {
    return () => {
      sortedMultiEditValueRef.current = undefined;
      unsubscribeRef.current && unsubscribeRef.current(action.role);
    };
    // On unmount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FlexBox horizontal right>
      <ActionButton
        icon="Search"
        title={t('search.multiThesaurus.button')}
        onClick={handleEditThesaurus}
        background="transparent"
      />
    </FlexBox>
  );
};
